import { useState, useMemo } from 'react';
import { TrendingDown, Sparkles, AlertCircle } from 'lucide-react';

/**
 * Calculadora de ROI — mostra ao visitante quanto dinheiro ele está perdendo
 * por clientes insatisfeitos que não voltam (sem o FaloComODono).
 *
 * Fórmula (modelo conservador validado):
 *   - Faturamento mensal = clientes_dia × ticket × 30
 *   - Curva de insatisfação por nota Google (8% até 40%)
 *   - Multiplicador de Lifetime Value (LTV) = 3x ticket
 *   - Apresenta apenas 30% da perda potencial como "recuperável" (defensável)
 *   - Cap de segurança: nunca mais que 45% do faturamento
 *
 * Fontes públicas: NRA 2024 (91% insatisfeitos não reclamam), HBR (LTV 5x).
 */

const SEGMENTS = [
  { id: 'restaurante', label: 'Restaurante', ticket: 50, ticketMin: 30, ticketMax: 200, clientes: 120 },
  { id: 'padaria', label: 'Padaria', ticket: 25, ticketMin: 10, ticketMax: 80, clientes: 250 },
  { id: 'lanchonete', label: 'Lanchonete / Hamburgueria', ticket: 35, ticketMin: 15, ticketMax: 120, clientes: 100 },
  { id: 'cafeteria', label: 'Cafeteria', ticket: 22, ticketMin: 10, ticketMax: 80, clientes: 90 },
  { id: 'pizzaria', label: 'Pizzaria / Delivery', ticket: 70, ticketMin: 30, ticketMax: 250, clientes: 80 },
  { id: 'salao', label: 'Salão de Beleza / Barbearia', ticket: 80, ticketMin: 30, ticketMax: 400, clientes: 25 },
  { id: 'clinica', label: 'Clínica / Consultório', ticket: 200, ticketMin: 80, ticketMax: 800, clientes: 15 },
  { id: 'pet', label: 'Pet Shop / Banho e Tosa', ticket: 90, ticketMin: 30, ticketMax: 350, clientes: 30 },
  { id: 'academia', label: 'Academia / Estúdio', ticket: 130, ticketMin: 60, ticketMax: 500, clientes: 40 },
  { id: 'comercio', label: 'Comércio / Varejo', ticket: 80, ticketMin: 20, ticketMax: 400, clientes: 70 },
  { id: 'outro', label: 'Outro tipo de negócio', ticket: 60, ticketMin: 15, ticketMax: 300, clientes: 60 },
];

function dissatisfactionRate(rating) {
  // Curva proporcional: 5.0 = 8%, 4.5 = 12%, 4.0 = 18%, 3.5 = 25%, 3.0 = 32%, 2.5 = 40%
  const r = Math.max(2.0, Math.min(5.0, rating));
  if (r >= 5.0) return 0.08;
  if (r >= 4.5) return 0.10 + (5.0 - r) * 0.04;
  if (r >= 4.0) return 0.14 + (4.5 - r) * 0.08;
  if (r >= 3.5) return 0.20 + (4.0 - r) * 0.10;
  if (r >= 3.0) return 0.27 + (3.5 - r) * 0.10;
  return 0.32 + (3.0 - r) * 0.16;
}

function formatBRL(v) {
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export const RoiCalculator = ({
  ctaText = 'Quero parar de perder dinheiro',
  ctaHref = '#pricing-section',
  variant = 'light', // 'light' | 'dark'
}) => {
  const [segmentId, setSegmentId] = useState('restaurante');
  const segment = useMemo(() => SEGMENTS.find(s => s.id === segmentId) || SEGMENTS[0], [segmentId]);

  const [clientes, setClientes] = useState(segment.clientes);
  const [ticket, setTicket] = useState(segment.ticket);
  const [rating, setRating] = useState(4.0);

  // Atualiza defaults ao trocar segmento
  const handleSegmentChange = (id) => {
    const seg = SEGMENTS.find(s => s.id === id);
    if (!seg) return;
    setSegmentId(id);
    setClientes(seg.clientes);
    setTicket(seg.ticket);
  };

  const calc = useMemo(() => {
    const faturamentoMes = clientes * ticket * 30;
    const taxaInsat = dissatisfactionRate(rating);
    const clientesPerdidos = clientes * 30 * taxaInsat;
    const perdaPotencial = clientesPerdidos * ticket * 3; // LTV 3x ticket
    // Mostro 30% da perda potencial como "recuperavel" (modelo defensavel)
    let recuperavel = perdaPotencial * 0.30;
    // Cap de seguranca: nunca mostra mais que 45% do faturamento
    const cap = faturamentoMes * 0.45;
    if (recuperavel > cap) recuperavel = cap;
    const planoCusto = 59.90;
    const lucroLiquido = recuperavel - planoCusto;
    return {
      faturamentoMes,
      taxaInsat,
      clientesPerdidos: Math.round(clientesPerdidos),
      recuperavel: Math.round(recuperavel),
      lucroLiquido: Math.round(lucroLiquido),
    };
  }, [clientes, ticket, rating]);

  const isDark = variant === 'dark';

  // Theme tokens
  const t = isDark ? {
    bgCard: 'bg-gradient-to-br from-[#0e3260] to-[#0A2647] border-[#C5A059]/20',
    text: 'text-white',
    textMute: 'text-white/60',
    label: 'text-white/80',
    sliderTrack: 'bg-white/10',
    inputBg: 'bg-white/5 border-white/10 text-white',
    resultBox: 'bg-white/5 border-white/10',
    resultLoss: 'text-rose-300',
    resultGain: 'text-emerald-300',
    cta: 'bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647]',
  } : {
    bgCard: 'bg-white border-slate-200 shadow-xl',
    text: 'text-[#0A2647]',
    textMute: 'text-slate-500',
    label: 'text-slate-700',
    sliderTrack: 'bg-slate-200',
    inputBg: 'bg-slate-50 border-slate-200 text-[#0A2647]',
    resultBox: 'bg-slate-50 border-slate-200',
    resultLoss: 'text-rose-600',
    resultGain: 'text-emerald-600',
    cta: 'bg-[#0A2647] hover:bg-[#0e3260] text-white',
  };

  return (
    <div className={`rounded-2xl border p-6 sm:p-8 ${t.bgCard}`} data-testid="roi-calculator">
      <div className="text-center mb-6">
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${isDark ? 'bg-[#C5A059]/15' : 'bg-[#C5A059]/10'} mb-3`}>
          <Sparkles className="w-3.5 h-3.5 text-[#C5A059]" />
          <span className="text-xs font-bold text-[#C5A059] tracking-wider uppercase">Calculadora Grátis</span>
        </div>
        <h3 className={`text-2xl sm:text-3xl font-extrabold ${t.text} mb-2`}>
          Quanto seu negócio está <span className="text-rose-500">perdendo agora</span>?
        </h3>
        <p className={`text-sm ${t.textMute} max-w-xl mx-auto`}>
          Descubra em 10 segundos quanto você pode estar deixando na mesa por clientes insatisfeitos que simplesmente não voltam.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-center">
        {/* INPUTS */}
        <div className="space-y-5">
          {/* Segmento */}
          <div>
            <label className={`text-xs font-bold uppercase tracking-wider ${t.label} mb-2 block`}>Tipo de negócio</label>
            <select
              value={segmentId}
              onChange={(e) => handleSegmentChange(e.target.value)}
              className={`w-full h-11 px-3 rounded-lg border ${t.inputBg} text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#C5A059]/40`}
              data-testid="roi-segment"
            >
              {SEGMENTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>

          {/* Clientes/dia */}
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <label className={`text-xs font-bold uppercase tracking-wider ${t.label}`}>Clientes por dia</label>
              <span className={`text-lg font-extrabold ${t.text}`} data-testid="roi-clientes-value">{clientes}</span>
            </div>
            <input
              type="range" min="10" max="800" step="5"
              value={clientes}
              onChange={(e) => setClientes(parseInt(e.target.value, 10))}
              className="w-full accent-[#C5A059] cursor-pointer"
              data-testid="roi-clientes"
            />
            <div className={`flex justify-between text-[10px] mt-1 ${t.textMute}`}>
              <span>10</span><span>800</span>
            </div>
          </div>

          {/* Ticket medio */}
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <label className={`text-xs font-bold uppercase tracking-wider ${t.label}`}>Ticket médio (R$)</label>
              <span className={`text-lg font-extrabold ${t.text}`} data-testid="roi-ticket-value">R$ {ticket}</span>
            </div>
            <input
              type="range" min={segment.ticketMin} max={segment.ticketMax} step="1"
              value={ticket}
              onChange={(e) => setTicket(parseInt(e.target.value, 10))}
              className="w-full accent-[#C5A059] cursor-pointer"
              data-testid="roi-ticket"
            />
            <div className={`flex justify-between text-[10px] mt-1 ${t.textMute}`}>
              <span>R$ {segment.ticketMin}</span><span>R$ {segment.ticketMax}</span>
            </div>
          </div>

          {/* Nota Google */}
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <label className={`text-xs font-bold uppercase tracking-wider ${t.label}`}>Sua nota atual no Google</label>
              <span className={`text-lg font-extrabold ${t.text} flex items-center gap-1`} data-testid="roi-rating-value">
                {rating.toFixed(1)} <span className="text-[#C5A059]">★</span>
              </span>
            </div>
            <input
              type="range" min="2.0" max="5.0" step="0.1"
              value={rating}
              onChange={(e) => setRating(parseFloat(e.target.value))}
              className="w-full accent-[#C5A059] cursor-pointer"
              data-testid="roi-rating"
            />
            <div className={`flex justify-between text-[10px] mt-1 ${t.textMute}`}>
              <span>2.0 ★</span><span>5.0 ★</span>
            </div>
          </div>
        </div>

        {/* RESULTADO */}
        <div className={`rounded-xl border p-5 sm:p-6 ${t.resultBox}`}>
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className={`w-4 h-4 ${t.resultLoss}`} />
                <p className={`text-xs font-bold uppercase tracking-wider ${t.resultLoss}`}>Receita potencialmente perdida</p>
              </div>
              <p className={`text-3xl sm:text-4xl font-extrabold ${t.resultLoss}`} data-testid="roi-result-loss">
                R$ {formatBRL(calc.recuperavel)}<span className="text-lg font-normal opacity-70">/mês</span>
              </p>
              <p className={`text-[11px] ${t.textMute} mt-1`}>
                Estimado: ~{calc.clientesPerdidos} clientes/mês saem insatisfeitos e não voltam
              </p>
            </div>

            <div className={`h-px ${isDark ? 'bg-white/10' : 'bg-slate-200'}`} />

            <div>
              <p className={`text-[11px] font-semibold uppercase tracking-wider ${t.textMute} mb-1`}>Custo do FaloComODono Empresarial</p>
              <p className={`text-xl font-bold ${t.text}`}>R$ 59,90<span className="text-sm opacity-60">/mês</span></p>
            </div>

            <div className={`rounded-lg p-3 ${isDark ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'}`}>
              <p className={`text-[11px] font-semibold uppercase tracking-wider ${t.resultGain} mb-1`}>💰 Lucro bruto potencial</p>
              <p className={`text-2xl sm:text-3xl font-extrabold ${t.resultGain}`} data-testid="roi-result-net">
                R$ {formatBRL(calc.lucroLiquido)}<span className="text-base font-normal opacity-70">/mês</span>
              </p>
            </div>

            <a
              href={ctaHref}
              className={`block text-center w-full ${t.cta} font-bold text-sm sm:text-base py-3.5 rounded-xl transition-all hover:scale-[1.02] active:scale-95`}
              data-testid="roi-cta"
            >
              {ctaText} →
            </a>
          </div>
        </div>
      </div>

      <div className={`mt-6 p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-slate-100'} flex items-start gap-2`}>
        <AlertCircle className={`w-3.5 h-3.5 ${t.textMute} flex-shrink-0 mt-0.5`} />
        <p className={`text-[10px] sm:text-[11px] ${t.textMute} leading-relaxed`}>
          <strong>Como calculamos:</strong> baseado em estudos publicados pela <strong>National Restaurant Association</strong> (relatório 2024 — 91% dos clientes insatisfeitos não reclamam, apenas deixam de voltar) e pela <strong>Harvard Business Review</strong> (o valor de vida útil — LTV — de um cliente fidelizado equivale a 3 a 5 vezes o valor da primeira compra). Apresentamos apenas <strong>30% do potencial estimado</strong> para manter uma projeção conservadora. O resultado real depende da gestão de cada negócio.
        </p>
      </div>
    </div>
  );
};

export default RoiCalculator;
